import { Capacitor } from '@capacitor/core';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { AppleSignIn } from '@capawesome/capacitor-apple-sign-in';
import { socialGoogle, socialApple } from './api';
import { setToken, setUser } from './auth';
import { GOOGLE_WEB_CLIENT_ID } from './config';

const WEB_CLIENT_ID = GOOGLE_WEB_CLIENT_ID;

export const isNative = () => Capacitor.getPlatform() !== 'web';
export const isIOS = () => Capacitor.getPlatform() === 'ios';

let googleReady = false;

const ensureGoogleInitialized = async () => {
  if (googleReady || !isNative()) return;
  if (!WEB_CLIENT_ID) throw new Error('Google sign-in is not configured.');
  // Web client id is passed on BOTH iOS and Android (Capawesome requirement).
  await GoogleSignIn.initialize({ clientId: WEB_CLIENT_ID });
  googleReady = true;
};

const persist = (data) => {
  setToken(data.token);
  setUser(data.user);
  return data.user;
};

// Native, in-app Google Sign-In (no external browser). Returns the app user.
export const nativeGoogleSignIn = async () => {
  await ensureGoogleInitialized();
  const result = await GoogleSignIn.signIn();
  const idToken = result?.idToken;
  if (!idToken) throw new Error('Google did not return a sign-in token.');
  const res = await socialGoogle(idToken);
  return persist(res.data);
};

const newNonce = () => {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

// Native Sign in with Apple (iOS only). Returns the app user.
export const nativeAppleSignIn = async () => {
  const nonce = newNonce();
  const result = await AppleSignIn.signIn({ scopes: ['EMAIL', 'FULL_NAME'], nonce });
  const identityToken = result?.idToken || result?.identityToken;
  if (!identityToken) throw new Error('Apple did not return an identity token.');
  const res = await socialApple({
    identityToken,
    nonce,
    givenName: result?.givenName || null,
    familyName: result?.familyName || null,
  });
  return persist(res.data);
};

export const isSignInCanceled = (err) => {
  const code = err?.code || err?.message || '';
  return /cancel/i.test(String(code));
};
